import WarningIcon from '@mui/icons-material/Warning';
import { Box, Typography } from '@mui/material';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import { themeColors } from 'toolkit/styles/colors';
import HtmlDisplayer from './html-displayer';

export const DEFAULT_DISPLAY_MESSAGE = 'Links in this message:';
export const HYPERLINK_REGEX = /(?:https:\/\/[^\s\<\>]+)/g;

export function trimFinalIfSpecialCharacter(str) {
  const finalCharacter = str[str.length - 1];
  if (finalCharacter < 'A' || finalCharacter > 'z') {
    return str.slice(0, -1);
  }
  return str;
}

interface HtmlDisplayerWithHyperlinksProps {
  note: string;
  displayMessage?: string;
}

export const HtmlDisplayerWithHyperlinks = ({
  note,
  displayMessage = DEFAULT_DISPLAY_MESSAGE,
}: HtmlDisplayerWithHyperlinksProps): JSX.Element => {
  const openConfirmationDialog = useConfirmationAlertDialog();

  const hyperLinks = note?.match(HYPERLINK_REGEX);

  function isEmptyHtml(note: string) {
    if (!note) {
      return true;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = note;
    const trimmedNote = tempDiv.textContent?.trim();
    return trimmedNote === '';
  }

  if (isEmptyHtml(note)) {
    return null;
  }

  async function handleClick(event, trimmedLink) {
    event.preventDefault();
    event.nativeEvent.stopImmediatePropagation();

    const shouldFollowHyperlink = await openConfirmationDialog({
      title: 'Opening external link',
      icon: <WarningIcon />,
      iconColor: 'error',
      message: (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="bodyLarge" component="div">
            A link is attempting to take you to:
          </Typography>
          <Typography
            variant="bodyLarge"
            component="div"
            color="primary"
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: themeColors.primary,
            }}
          >
            {trimmedLink}
          </Typography>
          <Typography variant="bodyLarge" component="div">
            Are you sure you want to proceed?
          </Typography>
        </Box>
      ),
      confirmationText: 'Confirm',
      confirmButtonColor: 'error',
    });

    if (shouldFollowHyperlink) {
      window.open(trimmedLink, '_blank');
    }
  }

  return (
    <Box>
      <HtmlDisplayer innerHtml={note}></HtmlDisplayer>
      {hyperLinks && (
        <Box sx={{ marginTop: '24px' }}>
          <Typography variant="labelLarge" component="div">
            {displayMessage}
          </Typography>
          {hyperLinks.map((link, index) => {
            const trimmedLink = trimFinalIfSpecialCharacter(link);
            return (
              <Box
                key={`${link}-${index}`}
                sx={{
                  '& .MuiTypography-root': {
                    margin: '0 12px',
                    padding: '4px 0',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: themeColors.primary,
                    textDecoration: 'underline',
                    '&:hover': {
                      cursor: 'pointer',
                      color: 'black',
                    },
                  },
                }}
              >
                <Typography
                  variant="bodyLarge"
                  component="div"
                  color="primary"
                  onClick={event => handleClick(event, trimmedLink)}
                >
                  {trimmedLink}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
